import { ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild } from '@angular/core';
import { NgClass, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MpCalendarComponent } from './mp-calendar/mp-calendar.component';
import { DatePickerUtils } from './mp-date-picker.utils';
import moment from 'moment';
import { DomEventListenerService } from '../../services/dom-event-listener.service';
import { TimeUtils } from '../../utils/time.utils';
import { DateType, FixedDate, IntervalDate } from '../../models/date.model';
import { DateUtils } from '../../utils/date.utils';
import { FixedTime, IntervalTime, TimeType } from '../../models/time.model';

@Component({
  selector: 'app-mp-date-picker',
  templateUrl: './mp-date-picker.component.html',
  imports: [
    NgIf,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MpCalendarComponent
  ],
  styleUrl: './mp-date-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MpDatePickerComponent implements OnInit, OnChanges, OnDestroy {

  /** 'small' | 'medium' | 'large' */
  @Input() public outlined: boolean = false;
  @Input() public outlinedColor: 'primary' | 'complementary' | 'accent' | 'yellow' = 'primary';
  @Input() public outlinedStyle: 'solid' | 'dashed' = 'solid';
  @Input() public size: 'small' | 'medium' | 'large' = 'large';
  @Input() public customClasses: string = '';
  @Input() public faIcon: string;
  @Input() public label: string;
  @Input() public disabled: boolean;
  @Input() public isReadOnly: boolean = false;
  @Input() public dateFormControl: FormControl;
  @Input() public timeFormControl: FormControl;
  @Input() public timeIntervalAllowed: boolean = false;
  @Input() public dateIntervalAllowed: boolean = false;
  @Input() public dateValue: FixedDate | IntervalDate;
  @Input() public timeValue: FixedTime | IntervalTime;
  @Input() public minDate: Date;
  @Input() public onlyBusinessDays: boolean = false;

  @Output() public dateValueChange = new EventEmitter<FixedDate | IntervalDate>();
  @Output() public timeValueChange = new EventEmitter<FixedTime | IntervalTime>();

  @ViewChild('calendarIcon') public calendarIcon: ElementRef;
  @ViewChild('datepickerField') public datepickerFieldElementRef: ElementRef;
  @ViewChild('dateInput') public dateInputFieldElementRef: ElementRef;
  @ViewChild('dateEndInput') public dateEndFieldElementRef: ElementRef;
  @ViewChild(MpCalendarComponent, { static: false, read: ElementRef }) private calendarComponentElementRef: ElementRef;

  public startDay: string;
  public startMonth: string;
  public startYear: string;

  public endDay: string;
  public endMonth: string;
  public endYear: string;

  public startDateValue: string;
  public endDateValue: string;

  public timeLabel: string;
  public startTime: string;
  public endTime: string;

  private valueChangeTimer: ReturnType<typeof setTimeout>;
  private blurTimer: ReturnType<typeof setTimeout>;
  private endBlurTimer: ReturnType<typeof setTimeout>;
  private readonly formControlSub: Subscription;

  private scrollListenerSubscription: Subscription;
  private clickListenerSubscription: Subscription;

  protected displayCalendar = signal(false);

  constructor(private readonly domListener: DomEventListenerService,
              private readonly ngZone: NgZone) {}

  ngOnInit() {
    this.subscribeToDomEvents();
    this.setValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dateFormControl'] || changes['timeFormControl']) {
      this.setValues();
    }
  }

  ngOnDestroy() {
    this.formControlSub?.unsubscribe();
    this.clickListenerSubscription?.unsubscribe();
    this.scrollListenerSubscription?.unsubscribe();

    this.removeDropdown();
  }

  public onDateBlur() {
    if (this.blurTimer) {
      clearTimeout(this.blurTimer);
    }
    this.blurTimer = setTimeout(() => {
      this.correctDate();
    }, 0);
  }

  public onEndDateBlur() {
    if (this.endBlurTimer) {
      clearTimeout(this.endBlurTimer);
    }
    this.endBlurTimer = setTimeout(() => {
      this.correctEndDate();
    }, 0);
  }

  public onValueChanged() {
    if (this.valueChangeTimer) {
      clearTimeout(this.valueChangeTimer);
    }

    if (this.disabled) {
      return;
    }

    this.valueChangeTimer = setTimeout(() => {

      // Handle date value updates
      const newDateValue = this.computeNewDateValue();
      if (this.dateFormControl) {
        this.dateFormControl.setValue(newDateValue);
        this.dateValueChange.emit(this.dateFormControl.value);
      } else {
        this.dateValueChange.emit(newDateValue);
      }

      // Handle time value updates
      const newTimeValue = this.computeNewTimeValue();
      if (this.timeFormControl) {
        this.timeFormControl.setValue(newTimeValue);
        this.timeValueChange.emit(this.timeFormControl.value);
      } else {
        this.timeValueChange.emit(newTimeValue);
      }
    }, 300);

  }


  public isValid() {
    return (!this.timeIntervalAllowed || (this.isHourValid(this.startTime) && this.isHourValid(this.endTime) &&
            this.isStartHourBeforeEndHour(this.startTime, this.endTime))) && this.isDateValid(this.startDateValue) &&
        (!this.dateIntervalAllowed || (this.isDateValid(this.endDateValue) &&
            this.isStartDateBeforeEndDate(this.startDateValue, this.endDateValue)));
  }

  private setValues() {
    if (this.dateValue) {
      this.setDatePickerValue(this.dateValue);
    } else {
      this.resetDatepicker();
    }

    this.initDateForm();
    this.initTimeForm();
  }

  // Helper function to compute the new date value
  private computeNewDateValue(): any {
    if (this.endDateValue) {
      const intervalDate = { type: DateType.INTERVAL_DATE } as IntervalDate;

      if (this.startYear || this.startMonth || this.startDay) {
        intervalDate.start = this.buildDate(+this.startYear, +this.startMonth, +this.startDay);
      }
      if (this.endYear || this.endMonth || this.endDay) {
        intervalDate.end = this.buildDate(+this.endYear, +this.endMonth, +this.endDay);
      }

      return intervalDate;
    } else if (this.startDateValue && (this.startYear || this.startMonth || this.startDay)) {
      return {
        type: DateType.FIXED_DATE,
        date: this.buildDate(+this.startYear, +this.startMonth, +this.startDay)
      };
    }

    return undefined;
  }

  // Helper function to compute the new time value
  private computeNewTimeValue(): any {
    if (this.startTime && this.endTime) {
      return {
        type: TimeType.INTERVAL_TIME,
        start: this.startTime,
        end: this.endTime
      };
    } else if (this.startTime || this.endTime) {
      return {
        type: TimeType.FIXED_TIME,
        time: this.startTime || this.endTime
      };
    }

    return undefined;
  }

  private buildDate(year: number, month: number, day: number): Date {
    return moment().year(year).month(month - 1).date(day).toDate();
  }

  private isDateValid(dateString: string) {
    if (dateString) {
      const splitted = dateString.split('/');
      if (splitted.length === 3) {
        const date = new Date(+splitted[2], +splitted[1] - 1, +splitted[0]);
        // If the date object is invalid it
        // will return 'NaN' on getTime()
        // and NaN is never equal to itself
        // eslint-disable-next-line no-self-compare
        return date.getTime() === date.getTime();
      }
    }
    return true;
  }

  private isStartDateBeforeEndDate(start: string, end: string) {
    if (start && end) {
      const startSplit = start.split('/');
      const endSplit = end.split('/');
      const startDate = new Date(+startSplit[2], +startSplit[1] - 1, +startSplit[0]);
      const endDate = new Date(+endSplit[2], +endSplit[1] - 1, +endSplit[0]);
      return startDate.getTime() <= endDate.getTime();
    }
    return true;
  }

  private isHourValid(hourString: string) {
    if (hourString) {
      const splitted = hourString.split(':');
      return splitted[0] && +splitted[0] >= 0 &&
          +splitted[0] <= 24 && splitted[1] &&
          +splitted[1] <= 60 && +splitted[1] >= 0;
    }
    return true;
  }

  private isStartHourBeforeEndHour(start: string, end: string) {
    if (start && end) {
      const startSplit = start.split(':');
      const endSplit = end.split(':');
      return startSplit[0] < endSplit[0] || (startSplit[0] === endSplit[0] && startSplit[1] <= endSplit[1]);
    }
    return true;
  }

  public openDatepicker() {
    if (this.disabled || this.displayCalendar()) {
      return;
    }
    this.displayCalendar.set(true);
  }

  private subscribeToDomEvents() {
    this.ngZone.runOutsideAngular(() => {
      this.scrollListenerSubscription = this.domListener.onScrollObservable.subscribe({
        next: (wEvent: WheelEvent) => {
          this.onDocumentScroll(wEvent);
        }
      });
      this.clickListenerSubscription = this.domListener.onClickObservable.subscribe({
        next: (mEvent: MouseEvent) => {
          this.onDocumentClick(mEvent);
        }
      });
    });
  }

  private setTimeValue(time: FixedTime | IntervalTime) {
    if (TimeUtils.isIntervalTime(time) && time.end) {
      this.setSingleTimeValue(time.start);
      this.setEndTimeValue(time.end);
    } else if (TimeUtils.isFixedTime(time)) {
      this.setSingleTimeValue(time.time);
      this.resetEndTime();
    }
  }

  private setSingleTimeValue(time: string) {

    if (time) {
      this.startTime = TimeUtils.mapTimeToHoursAndSeconds(time);
    } else {
      this.startTime = undefined;
    }
    this.refreshHourLabel();
  }


  private setEndTimeValue(time: string) {
    if (time) {
      this.endTime = TimeUtils.mapTimeToHoursAndSeconds(time);
    } else {
      this.endTime = undefined;
    }
    this.refreshHourLabel();
  }

  private refreshHourLabel() {
    if (this.startTime && this.endTime) {
      this.timeLabel = `${this.startTime} - ${this.endTime}`;
    } else if (this.startTime || this.endTime) {
      this.timeLabel = `${this.startTime ? this.startTime : ''}${this.endTime ? this.endTime : ''}`;
    }
  }

  public setDatePickerValue(date: FixedDate | IntervalDate) {
    if (DateUtils.isIntervalDate(date) && date.end) {
      this.setSingleDateValue(date.start);
      this.setEndDateValue(date.end);
    } else if (DateUtils.isFixedDate(date)) {
      this.setSingleDateValue(date.date as Date);
      this.resetEndDate();
    }
  }

  private setSingleDateValue(date: Date) {
    if (date) {
      const momentDate = moment(date);
      this.startDay = DatePickerUtils.addPaddingToDatesFields(momentDate.date(), 2);
      this.startMonth = DatePickerUtils.addPaddingToDatesFields(momentDate.month() + 1, 2);
      this.startYear = DatePickerUtils.addPaddingToYearFields(momentDate.year(), 4);
      this.startDateValue = `${this.startDay}/${this.startMonth}/${this.startYear}`;
    } else {
      this.startDay = undefined;
      this.startMonth = undefined;
      this.startYear = undefined;
      this.startDateValue = undefined;
    }
  }

  private setEndDateValue(date: Date) {
    if (date) {
      const momentDate = moment(date);
      this.endDay = DatePickerUtils.addPaddingToDatesFields(momentDate.date(), 2);
      this.endMonth = DatePickerUtils.addPaddingToDatesFields(momentDate.month() + 1, 2);
      this.endYear = DatePickerUtils.addPaddingToYearFields(momentDate.year(), 4);
      this.endDateValue = `${this.endDay}/${this.endMonth}/${this.endYear}`;
    } else {
      this.endDay = undefined;
      this.endMonth = undefined;
      this.endYear = undefined;
      this.endDateValue = undefined;
    }
  }


  public resetDatepicker() {
    this.resetStartDate();
    this.resetEndDate();
    this.resetTime();
    this.onValueChanged();
  }

  private resetStartDate() {
    this.startDay = undefined;
    this.startMonth = undefined;
    this.startYear = undefined;
    this.startDateValue = undefined;
  }

  private resetEndDate() {
    this.endDay = undefined;
    this.endMonth = undefined;
    this.endYear = undefined;
    this.endDateValue = undefined;
  }

  private resetTime() {
    this.resetStartTime();
    this.resetEndTime();
    this.timeLabel = undefined;
  }

  protected resetStartTime() {
    this.startTime = undefined;
    this.refreshHourLabel();
  }

  protected resetEndTime() {
    this.endTime = undefined;
    this.refreshHourLabel();
  }

  public datePicked(dateValue: FixedDate | IntervalDate, time: FixedTime | IntervalTime) {
    this.setDatePickerValue(dateValue);
    if (this.timeIntervalAllowed) {
      this.setTimeValue(time);
    }
    this.onValueChanged();
    this.removeDropdown();
    this.focus();
  }

  public removeDropdown(): void {
    this.displayCalendar.set(false);
  }

  public focus() {
    this.datepickerFieldElementRef?.nativeElement?.focus();
  }

  private initDateForm() {
    if (this.dateFormControl?.value) {
      const dateValue = this.dateFormControl.value;
      if (DateUtils.isFixedDate(dateValue)) {
        this.setDatePickerValue(dateValue as FixedDate);
      } else if (DateUtils.isIntervalDate(dateValue)) {
        this.setDatePickerValue(dateValue as IntervalDate);
      }
    }
  }

  private initTimeForm() {
    if (this.timeIntervalAllowed && this.timeFormControl?.value) {
      const timeValue = this.timeFormControl.value;
      if (TimeUtils.isFixedTime(timeValue)) {
        this.setTimeValue(timeValue as FixedTime);
      } else if (TimeUtils.isIntervalTime(timeValue)) {
        this.setTimeValue(timeValue as IntervalTime);
      }
    }
  }

  private correctDate() {
    if (this.startDateValue && this.startDateValue.length) {
      let splitted: string[] = [];
      if (this.startDateValue.includes('/')) {
        splitted = this.startDateValue.trim().split('/');
      } else if (this.startDateValue.includes('.')) {
        splitted = this.startDateValue.trim().split('.');
      } else if (this.startDateValue.includes('-')) {
        splitted = this.startDateValue.trim().split('-');
      } else if (this.startDateValue.length >= 6) {
        const trimed = this.startDateValue.trim();
        splitted.push(trimed.slice(0, 2));
        splitted.push(trimed.slice(2, 4));
        splitted.push(trimed.slice(4));
      }

      if (splitted[0]) {
        this.startDay = splitted[0];
      }
      if (splitted[1]) {
        this.startMonth = splitted[1];
      }
      if (splitted[2]) {
        if (splitted[2].length === 2) {
          splitted[2] = `20${splitted[2]}`;
        } else if (splitted[2].length === 3) {
          splitted[2] = `2${splitted[2]}`;
        }
        this.startYear = splitted[2];
      } else {
        this.startYear = `${moment().year()}`;
      }

      if (this.startYear && !isNaN(+this.startYear) &&
          this.startMonth && !isNaN(+this.startMonth) &&
          this.startDay && !isNaN(+this.startDay)) {
        const correctDate = moment().year(+this.startYear).month(+this.startMonth - 1).date(+this.startDay).toDate();
        this.setSingleDateValue(correctDate);
        this.onValueChanged();
      }
    } else {
      this.startYear = undefined;
      this.startMonth = undefined;
      this.startDay = undefined;
      this.onValueChanged();
    }
  }

  private correctEndDate() {
    if (this.endDateValue && this.endDateValue.length) {
      let splitted: string[] = [];
      if (this.endDateValue.includes('/')) {
        splitted = this.endDateValue.trim().split('/');
      } else if (this.endDateValue.includes('.')) {
        splitted = this.endDateValue.trim().split('.');
      } else if (this.endDateValue.includes('-')) {
        splitted = this.endDateValue.trim().split('-');
      } else if (this.endDateValue.length >= 6) {
        const trimed = this.endDateValue.trim();
        splitted.push(trimed.slice(0, 2));
        splitted.push(trimed.slice(2, 4));
        splitted.push(trimed.slice(4));
      }

      if (splitted[0]) {
        this.endDay = splitted[0];
      }
      if (splitted[1]) {
        this.endMonth = splitted[1];
      }
      if (splitted[2]) {
        if (splitted[2].length === 2) {
          splitted[2] = `20${splitted[2]}`;
        } else if (splitted[2].length === 3) {
          splitted[2] = `2${splitted[2]}`;
        }
        this.endYear = splitted[2];
      } else {
        this.endYear = `${moment().year()}`;
      }

      if (this.endYear && !isNaN(+this.endYear) &&
          this.endMonth && !isNaN(+this.endMonth) &&
          this.endDay && !isNaN(+this.endDay)) {
        const correctEndDate = moment().year(+this.endYear).month(+this.endMonth - 1).date(+this.endDay).toDate();
        this.setEndDateValue(correctEndDate);
        this.onValueChanged();
      }
    } else {
      this.endYear = undefined;
      this.endMonth = undefined;
      this.endDay = undefined;
      this.onValueChanged();
    }
  }

  private blur() {
    this.datepickerFieldElementRef?.nativeElement?.blur();
    this.dateInputFieldElementRef?.nativeElement?.blur();
    this.dateEndFieldElementRef?.nativeElement?.blur();
  }

  private onDocumentClick(event): void {
    if (this.calendarComponentElementRef && !this.calendarComponentElementRef.nativeElement.contains(event.target) &&
        !this.calendarIcon.nativeElement.contains(event.target)) {
      this.ngZone.run(() => this.removeDropdown());
    }
  }

  private onDocumentScroll(event) {
    if (this.calendarComponentElementRef && !this.calendarComponentElementRef?.nativeElement.contains(event.target)) {
      this.ngZone.run(() => this.removeDropdown());
    }

    if (this.datepickerFieldElementRef && !this.datepickerFieldElementRef.nativeElement.contains(event.target)) {
      this.ngZone.run(() => this.blur());
    }
  }

}
